import { useEffect, useMemo, useState } from "react";
import { API_BASE, getAuthHeaders } from "../../api/client";
import { dayOffPeopleCostPlan } from "../../lib/softoneSlotSync";

function formatOfferDate(iso, lang) {
  const [year, month, day] = String(iso || "").split("-").map(Number);
  if (!year || !month || !day) return iso;
  return new Date(year, month - 1, day).toLocaleDateString(lang === "gr" ? "el-GR" : "en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

function reasonText(reason, lang) {
  if (reason === "not_future") {
    return lang === "gr"
      ? "Η πρόταση άδειας ισχύει από αύριο και μετά."
      : "A day-off offer is only for tomorrow and later.";
  }
  return lang === "gr" ? "Η ημέρα δεν είναι ανοιχτή για πρόταση άδειας." : "This day is not open for a day-off offer.";
}

function blockLabel(block, lang) {
  if (block === "no_email") return lang === "gr" ? "Χωρίς email" : "No email";
  if (block === "opted_out") return lang === "gr" ? "Email κλειστό" : "Email off";
  return null;
}

function replyLabel(reply, notified, lang) {
  if (reply === "accepted") return lang === "gr" ? "Θα την πάρει" : "Will take it";
  if (reply === "declined") return lang === "gr" ? "Δεν θα την πάρει" : "Won't take it";
  if (notified) return lang === "gr" ? "Ειδοποιήθηκε, χωρίς απάντηση" : "Notified, no reply yet";
  return null;
}

function formatPct(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return `${Math.max(0, n).toFixed(1)}%`;
}

function leavesNeededText(plan, lang) {
  if (!plan || plan.leavesNeeded == null || plan.leavesNeeded < 1 || plan.currentPct == null) return null;
  if (!plan.reachable) {
    const stayed = formatPct(plan.afterAllPct);
    return lang === "gr"
      ? `Ακόμα κι αν πάρουν άδεια όλοι (${plan.leavesNeeded}), το κόστος προσωπικού μένει στο ${stayed}.`
      : `Even if all ${plan.leavesNeeded} take leave, people cost stays at ${stayed}.`;
  }
  if (plan.leavesNeeded === 1) {
    return lang === "gr" ? "1 εργαζόμενος πρέπει να πάρει άδεια." : "1 employee should take leave.";
  }
  return lang === "gr"
    ? `${plan.leavesNeeded} εργαζόμενοι πρέπει να πάρουν άδεια.`
    : `${plan.leavesNeeded} employees should take leave.`;
}

function statusLabel(status, lang) {
  if (status === "sent") return lang === "gr" ? "Στάλθηκε" : "Sent";
  if (status === "failed") return lang === "gr" ? "Απέτυχε το email" : "Email failed";
  if (status === "not_eligible") return lang === "gr" ? "Χωρίς υπόλοιπο" : "No days left";
  return blockLabel(status, lang) || status;
}

export function DayOffOfferDialog({
  open,
  date,
  storeId,
  unifiedLocationId,
  employeeCodes,
  specialties,
  shifts,
  revenue,
  labourCostEur,
  thresholdPct,
  lang,
  onClose,
  onSent,
}) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (!open || !date) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setError("");
    setPayload(null);
    setResults(null);
    setSelected(new Set());
    const params = new URLSearchParams();
    params.set("offer_date", date);
    if (storeId) params.set("store_id", storeId);
    if (unifiedLocationId != null) params.set("unified_location_id", String(unifiedLocationId));
    (employeeCodes || []).forEach((code) => params.append("employee_codes", code));
    fetch(`${API_BASE}/schedule/day-off-offers?${params}`, {
      headers: getAuthHeaders(),
      signal: controller.signal,
    })
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          const detail = body?.detail;
          throw new Error(typeof detail === "string" ? detail : "Could not load day-off candidates");
        }
        return body;
      })
      .then((body) => {
        if (!controller.signal.aborted) setPayload(body);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err?.message || "Could not load day-off candidates");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [open, date, storeId, unifiedLocationId, employeeCodes]);

  const candidateList = payload?.candidates;
  const candidates = candidateList || [];
  const candidateCodes = useMemo(
    () => new Set((candidateList || []).map((row) => String(row.employee_code || "").trim()).filter(Boolean)),
    [candidateList],
  );
  const plan = useMemo(() => dayOffPeopleCostPlan({
    revenue,
    labourCostEur,
    thresholdPct,
    shifts: (shifts || []).map((shift) => ({
      ...shift,
      removable: candidateCodes.has(String(shift.code || "").trim()),
    })),
  }), [revenue, labourCostEur, thresholdPct, shifts, candidateCodes]);
  const neededText = leavesNeededText(plan, lang);
  const selectedPct = useMemo(() => {
    if (!plan || plan.currentPct == null || selected.size === 0) return null;
    const removed = [...selected].reduce((sum, code) => sum + (Number(plan.scaledCostByCode[code]) || 0), 0);
    return ((plan.labourCostEur - removed) / plan.revenue) * 100;
  }, [plan, selected]);

  if (!open) return null;

  const otherNotified = (payload?.notified || []).filter((row) => !candidateCodes.has(String(row.employee_code || "").trim()));
  const when = formatOfferDate(date, lang);

  function toggle(code) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  async function send() {
    const codes = [...selected];
    if (!codes.length || sending) return;
    setSending(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/schedule/day-off-offers`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          store_id: storeId || undefined,
          unified_location_id: unifiedLocationId,
          offer_date: date,
          employee_codes: codes,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = body?.detail;
        throw new Error(typeof detail === "string" ? detail : "Could not send the offers");
      }
      setResults(body.results || []);
      if (typeof onSent === "function") onSent();
      setPayload((prev) => {
        if (!prev) return prev;
        const sent = new Set((body.results || []).filter((row) => row.email_status === "sent").map((row) => row.employee_code));
        return {
          ...prev,
          candidates: (prev.candidates || []).map((row) => (
            sent.has(row.employee_code) ? { ...row, already_notified: true, email_status: "sent" } : row
          )),
        };
      });
      setSelected(new Set());
    } catch (err) {
      setError(err?.message || "Could not send the offers");
    } finally {
      setSending(false);
    }
  }

  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.45)",
        zIndex: 80,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={lang === "gr" ? "Πρόταση άδειας" : "Offer a day off"}
        onClick={(event) => event.stopPropagation()}
        style={{
          width: "min(440px, 100%)",
          maxHeight: "min(80vh, 640px)",
          overflow: "auto",
          background: "var(--surface)",
          color: "var(--text-primary)",
          borderRadius: 16,
          border: "1px solid var(--border)",
          boxShadow: "0 16px 40px rgba(0,0,0,0.18)",
          padding: 20,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800 }}>
              {lang === "gr" ? "Πρόταση άδειας" : "Offer a day off"}
            </div>
            <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>{when}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={lang === "gr" ? "Κλείσιμο" : "Close"}
            style={{ border: "none", background: "transparent", fontSize: 18, cursor: "pointer", color: "var(--text-muted)" }}
          >
            ×
          </button>
        </div>

        <p style={{ fontSize: 13, lineHeight: 1.45, margin: "12px 0 8px" }}>
          {lang === "gr"
            ? "Διάλεξε ποιοι θα πάρουν ειδοποίηση να πάρουν ετήσια άδεια."
            : "Choose who should be asked to take annual leave."}
        </p>
        {neededText ? (
          <div style={{ fontSize: 13, fontWeight: 800, lineHeight: 1.4, marginBottom: 8 }}>
            {neededText}
          </div>
        ) : null}
        {selectedPct != null && plan.currentPct != null ? (
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 10 }}>
            {lang === "gr"
              ? `Αν πάρουν άδεια οι επιλεγμένοι: ${formatPct(plan.currentPct)} → ${formatPct(selectedPct)}`
              : `If the selected people take leave: ${formatPct(plan.currentPct)} → ${formatPct(selectedPct)}`}
          </div>
        ) : null}

        {loading ? (
          <div style={{ fontSize: 13, color: "var(--text-muted)", padding: "12px 0" }}>
            {lang === "gr" ? "Φόρτωση…" : "Loading…"}
          </div>
        ) : null}

        {error ? (
          <div style={{ fontSize: 13, color: "#B91C1C", background: "#FEF2F2", borderRadius: 8, padding: "8px 10px", marginBottom: 10 }}>
            {error}
          </div>
        ) : null}

        {!loading && payload && !payload.eligible ? (
          <div style={{ fontSize: 13, lineHeight: 1.45 }}>
            {reasonText(payload.reason, lang)}
          </div>
        ) : null}

        {!loading && payload?.eligible && (employeeCodes || []).length === 0 ? (
          <div style={{ fontSize: 13, lineHeight: 1.45 }}>
            {lang === "gr"
              ? "Κανείς δεν είναι προγραμματισμένος αυτή την ημέρα."
              : "Nobody is scheduled to work this day."}
          </div>
        ) : null}

        {!loading && payload?.eligible && (employeeCodes || []).length > 0 && candidates.length === 0 ? (
          <div style={{ fontSize: 13, lineHeight: 1.45 }}>
            {lang === "gr"
              ? "Κανείς από όσους είναι στο πρόγραμμα αυτή την ημέρα δεν έχει υπόλοιπο ετήσιας άδειας."
              : "Nobody scheduled this day still has annual leave days left."}
          </div>
        ) : null}

        {!loading && otherNotified.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 10 }}>
            {otherNotified.map((row) => (
              <div
                key={row.employee_code}
                style={{
                  padding: "8px 10px",
                  border: "1px solid var(--border)",
                  borderRadius: 10,
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 14 }}>{row.name}</div>
                {specialties?.[row.employee_code] ? (
                  <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-secondary)", marginTop: 1 }}>{specialties[row.employee_code]}</div>
                ) : null}
                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                  {replyLabel(row.reply, true, lang)}
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {!loading && candidates.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {[...candidates].sort((a, b) => (
              (Number(plan.scaledCostByCode?.[b.employee_code]) || 0)
              - (Number(plan.scaledCostByCode?.[a.employee_code]) || 0)
            )).map((row) => {
              const blocked = blockLabel(row.notify_block, lang);
              const disabled = !row.can_notify;
              return (
                <label
                  key={row.employee_code}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 10px",
                    border: "1px solid var(--border)",
                    borderRadius: 10,
                    opacity: disabled ? 0.55 : 1,
                    cursor: disabled ? "not-allowed" : "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    disabled={disabled || sending}
                    checked={selected.has(row.employee_code)}
                    onChange={() => toggle(row.employee_code)}
                  />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block", fontWeight: 700, fontSize: 14 }}>{row.name}</span>
                    {specialties?.[row.employee_code] ? (
                      <span style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--text-secondary)", marginTop: 1 }}>{specialties[row.employee_code]}</span>
                    ) : null}
                    <span style={{ display: "block", fontSize: 12, color: "var(--text-muted)" }}>
                      {lang === "gr"
                        ? `${row.days_left} ημέρες υπόλοιπο`
                        : `${row.days_left} day${row.days_left === 1 ? "" : "s"} left`}
                      {plan.pctIfOnly?.[row.employee_code] != null && plan.currentPct != null
                        ? ` · ${formatPct(plan.currentPct)} → ${formatPct(plan.pctIfOnly[row.employee_code])}`
                        : ""}
                      {replyLabel(row.reply, row.already_notified, lang) ? ` · ${replyLabel(row.reply, row.already_notified, lang)}` : ""}
                      {blocked ? ` · ${blocked}` : ""}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        ) : null}

        {results?.length ? (
          <ul style={{ margin: "12px 0 0", paddingLeft: 18, fontSize: 12, color: "var(--text-muted)" }}>
            {results.map((row) => (
              <li key={row.employee_code}>
                {row.name}: {statusLabel(row.email_status, lang)}
              </li>
            ))}
          </ul>
        ) : null}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: "1px solid var(--border)",
              background: "var(--surface)",
              borderRadius: 10,
              padding: "8px 12px",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {lang === "gr" ? "Κλείσιμο" : "Close"}
          </button>
          <button
            type="button"
            onClick={send}
            disabled={sending || selected.size === 0 || !payload?.eligible}
            style={{
              border: "none",
              background: "var(--green, #15803D)",
              color: "#fff",
              borderRadius: 10,
              padding: "8px 12px",
              fontWeight: 800,
              cursor: sending || selected.size === 0 ? "not-allowed" : "pointer",
              opacity: sending || selected.size === 0 || !payload?.eligible ? 0.5 : 1,
            }}
          >
            {sending
              ? (lang === "gr" ? "Αποστολή…" : "Sending…")
              : (lang === "gr" ? "Αποστολή ειδοποίησης" : "Send notification")}
          </button>
        </div>
      </div>
    </div>
  );
}
